import { CircleHelp } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { MetricCatalogEntry } from "../types";

interface InfoTipProps {
  entry?: MetricCatalogEntry;
  label?: string;
  children?: React.ReactNode;
}

export function InfoTip({ entry, label, children }: InfoTipProps) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<"above" | "below">("above");
  const id = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLSpanElement>(null);
  const ignoreNextFocus = useRef(false);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        ignoreNextFocus.current = true;
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const updatePlacement = () => {
      const button = buttonRef.current;
      const popover = popoverRef.current;
      if (!button || !popover) return;
      const buttonRect = button.getBoundingClientRect();
      const popoverHeight = popover.getBoundingClientRect().height;
      setPlacement(buttonRect.top < popoverHeight + 16 ? "below" : "above");
    };
    updatePlacement();
    window.addEventListener("resize", updatePlacement);
    window.addEventListener("scroll", updatePlacement, true);
    return () => {
      window.removeEventListener("resize", updatePlacement);
      window.removeEventListener("scroll", updatePlacement, true);
    };
  }, [open]);

  const title = entry?.client_label ?? label ?? "About this evidence";
  return (
    <span
      className="info-tip"
      ref={wrapperRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        ref={buttonRef}
        type="button"
        className="info-button"
        aria-label={`About ${title}`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
        onFocus={() => {
          if (ignoreNextFocus.current) {
            ignoreNextFocus.current = false;
            return;
          }
          setOpen(true);
        }}
      >
        <CircleHelp size={15} strokeWidth={1.9} aria-hidden="true" />
      </button>
      {open && (
        <span ref={popoverRef} className={`info-popover info-popover--${placement}`} id={id} role="tooltip">
          <strong>{title}</strong>
          {entry ? (
            <>
              <span>{entry.definition}</span>
              <span><b>How to read:</b> {entry.how_to_read}</span>
              <span><b>Example:</b> {entry.worked_example}</span>
              <span className="claim-boundary">{entry.claim_boundary}</span>
            </>
          ) : (
            children
          )}
        </span>
      )}
    </span>
  );
}
